import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Download, Eye, FileText, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useDocuments } from '../hooks/useDocuments';
import { FileMetadata } from '../types/file.types';

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getFileTypeLabel = (mimeType: string): string => {
  if (mimeType.includes('pdf')) return 'PDF';
  if (mimeType.includes('png')) return 'PNG';
  if (mimeType.includes('jpeg') || mimeType.includes('jpg')) return 'JPG';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'DOC';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'XLS';
  return 'FILE';
};

export const FileList = () => {
  const { documents, isLoading, deleteDocument, getPreviewUrl } = useDocuments();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<FileMetadata | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handlePreview = async (document: FileMetadata) => {
    setPreviewLoading(document.id);
    const url = await getPreviewUrl(document.id);
    if (url) {
      window.open(url, '_blank');
    }
    setPreviewLoading(null);
  };

  const handleDownload = async (doc: FileMetadata) => {
    const url = await getPreviewUrl(doc.id);
    if (url) {
      const link = window.document.createElement('a');
      link.href = url;
      link.download = doc.name;
      link.click();
    }
  };

  const handleDeleteClick = (document: FileMetadata) => {
    setFileToDelete(document);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (fileToDelete) {
      setIsDeleting(true);
      await deleteDocument(fileToDelete.id);
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setFileToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <>
        <div className="hidden sm:block rounded-md border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="min-w-[200px] text-left">Name</TableHead>
                <TableHead className="w-[80px] text-left">Type</TableHead>
                <TableHead className="w-[100px] text-right">Size</TableHead>
                <TableHead className="min-w-[140px] text-left">Uploaded</TableHead>
                <TableHead className="text-right w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-lg shrink-0" />
                      <Skeleton className="h-4 w-40" />
                    </div>
                  </TableCell>
                  <TableCell><Skeleton className="h-5 w-12 rounded-md" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-14" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Skeleton className="h-9 w-9 rounded-md" />
                      <Skeleton className="h-9 w-9 rounded-md" />
                      <Skeleton className="h-9 w-9 rounded-md" />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="sm:hidden space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <div className="flex items-start gap-3">
                <Skeleton className="h-10 w-10 rounded-lg shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                <Skeleton className="h-9 flex-1 rounded-md" />
                <Skeleton className="h-9 flex-1 rounded-md" />
              </div>
            </Card>
          ))}
        </div>
      </>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-muted w-16 h-16 flex items-center justify-center mb-4">
          <FileText className="h-8 w-8 text-muted-foreground" />
        </div>
        <p className="font-medium text-foreground">No documents uploaded yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Upload your first document using the dropzone above
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden sm:block rounded-md border border-border overflow-hidden content-loaded">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent sticky top-0 z-10 bg-card border-b border-border shadow-[0_1px_0_0_hsl(var(--border))]">
                <TableHead className="min-w-[200px] text-left">Name</TableHead>
                <TableHead className="w-[80px] text-left">Type</TableHead>
                <TableHead className="w-[100px] text-right">Size</TableHead>
                <TableHead className="min-w-[140px] text-left">Uploaded</TableHead>
                <TableHead className="text-right w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                      <FileText className="h-4 w-4 text-primary" />
                    </div>
                    <span className="truncate font-medium">{doc.name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">{getFileTypeLabel(doc.mimeType)}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-right tabular-nums">
                  {formatFileSize(doc.size)}
                </TableCell>
                <TableCell className="text-muted-foreground text-left">
                  {doc.createdAt ? formatDate(doc.createdAt) : '—'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handlePreview(doc)}
                          disabled={previewLoading === doc.id}
                          className="h-9 w-9"
                        >
                          {previewLoading === doc.id ? (
                            <Spinner size="sm" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p className="font-medium">Preview</p>
                        <p className="text-muted-foreground text-xs">Open document in new tab</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleDownload(doc)}
                          className="h-9 w-9"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p className="font-medium">Download</p>
                        <p className="text-muted-foreground text-xs">Save file to your device</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => handleDeleteClick(doc)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10 h-9 w-9"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p className="font-medium">Delete</p>
                        <p className="text-muted-foreground text-xs">Permanently remove this document</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden space-y-3">
        {documents.map((doc) => (
          <Card key={doc.id} className="p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary/10 p-2.5 shrink-0">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{doc.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary" className="text-xs">
                    {getFileTypeLabel(doc.mimeType)}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {formatFileSize(doc.size)}
                  </span>
                </div>
                {doc.createdAt && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(doc.createdAt)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handlePreview(doc)}
                disabled={previewLoading === doc.id}
                className="gap-1.5"
              >
                {previewLoading === doc.id ? (
                  <Spinner size="sm" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                View
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownload(doc)}
                className="gap-1.5"
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteClick(doc)}
                className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{fileToDelete?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="gap-2">
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} loading={isDeleting} loadingLabel="Deleting..." className="gap-2">
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
